import { useState, useEffect, useRef } from "react";
import { TIER_COLORS, TIER_ORDER, DAYS, TIME_SLOTS } from "../utils/tiers";
import { getGroupSchedule, getEndTime } from "../utils/time";
import { getWarnings, hasAvailabilityConflict } from "../utils/validation";
import { FOLLOW_UPS, hasFollowUp, toggleFollowUp } from "../utils/followUps";
import { groupsWaitingFor, hasSpace, waitlistPosition, isWaiting } from "../utils/waitlist";
import { isDeferred, hasNoFit } from "../utils/placement";
import { otherClinicsForPlayer } from "../utils/membership";
import { planRename, duplicateNames } from "../utils/renamePlayer";
import { PendingRequestList } from "./PendingChanges";

export default function PlayerModal({ player, players = [], groups, movePlayer, updatePlayer, onWarnings, onClose, onWaitlistAdd, onWaitlistRemove, onOpenGroup, onRemoveFromGroup, pendingRequests = [], currentUser, onApproveRequest, onRejectRequest, onCancelRequest }) {
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [emailError, setEmailError] = useState(null);
  const [nameError, setNameError] = useState(null);
  // Notes are editable without entering Edit mode — jotting down what a member
  // said about their schedule is the most frequent thing done on this card, and
  // it shouldn't cost an Edit/Save round-trip. Kept in local state and committed
  // on blur so typing isn't re-rendered against the saved value on every key.
  const [noteDraft, setNoteDraft] = useState(player.notes || "");
  const [noteSaved, setNoteSaved] = useState(false);
  const savedTimer = useRef(null);

  // Re-seed when the modal is pointed at a different member, and pick up an
  // edit made through Edit mode (which writes notes via the same field).
  useEffect(() => {
    setNoteDraft(player.notes || "");
  }, [player.id, player.notes]);

  useEffect(() => () => clearTimeout(savedTimer.current), []);

  function commitNote() {
    const next = noteDraft.trim();
    if (next === (player.notes || "").trim()) return;
    updatePlayer(player.id, { notes: next });
    setNoteSaved(true);
    clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => setNoteSaved(false), 1800);
  }

  const colors = TIER_COLORS[player.tier] || TIER_COLORS["No Group"];

  const availByDay = Object.fromEntries(DAYS.map(d => [d, []]));
  player.availability.forEach(slot => {
    const [day, time] = slot.split("|");
    if (availByDay[day]) availByDay[day].push(time);
  });
  const hasDays = DAYS.some(d => availByDay[d].length > 0);

  const currentGroup = groups.find(g => g.id === player.groupId);
  // Clinics beyond the primary one. A member can be enrolled in more than one,
  // and the card is the only place that shows the whole picture.
  const extraClinics = otherClinicsForPlayer(player.id, groups, player.groupId);

  // Everyone else on this member's household id (blank ids never group).
  const householdId = String(player.householdId ?? "").trim();
  const household = householdId
    ? players.filter(p => p.id !== player.id && String(p.householdId ?? "").trim() === householdId)
    : [];

  const groupMembers = currentGroup
    ? currentGroup.playerIds
        .filter(id => id !== player.id)
        .map(id => players.find(p => p.id === id))
        .filter(Boolean)
    : [];

  function groupMembersOf(g) {
    return g.playerIds.map(id => players.find(p => p.id === id)).filter(Boolean);
  }

  const searchQuery = search.trim().toLowerCase();

  const filteredGroups = groups
    .filter(g => g.tier !== "No Group")
    .filter(g => {
      if (!searchQuery) return true;
      return (
        g.id.toLowerCase().includes(searchQuery) ||
        g.tier.toLowerCase().includes(searchQuery) ||
        (g.day && g.day.toLowerCase().includes(searchQuery)) ||
        groupMembersOf(g).some(p => p.name.toLowerCase().includes(searchQuery))
      );
    })
    .sort((a, b) => TIER_ORDER.indexOf(a.tier) - TIER_ORDER.indexOf(b.tier));

  function handleAssign(targetGroup) {
    // Already a member — primary or second clinic — so there's nothing to do.
    if ((targetGroup.playerIds || []).includes(player.id)) return;
    const warnings = getWarnings(player, targetGroup);
    if (warnings.length > 0 && onWarnings) onWarnings(warnings);
    movePlayer(player.id, player.groupId, targetGroup.id);
    onClose();
  }

  function startEditing() {
    setDraft({
      name: player.name || "",
      email: player.email || "",
      householdId: player.householdId ?? "",
      age: player.age ?? "",
      gender: player.gender || "",
      tier: player.tier,
      skillLevel: player.skillLevel || "",
      availability: [...player.availability],
      preferredPartners: player.preferredPartners || "",
      notes: player.notes || "",
      privateTimes: player.privateTimes || "",
    });
    setEditing(true);
  }

  function cancelEditing() {
    setEditing(false);
    setDraft(null);
    setEmailError(null);
    setNameError(null);
  }

  function saveEditing() {
    const email = draft.email.trim();
    // Clearing the address is allowed (some members genuinely have none);
    // anything else has to look like an address, since a typo silently
    // breaks both the schedule mail-out and the "My Schedule" lookup.
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError("That doesn't look like an email address.");
      return;
    }
    // A member with no name can't be found again in any of the pickers, so
    // this is the one field the form won't let you empty.
    if (!draft.name.trim()) {
      setNameError("A name is required.");
      return;
    }

    // A rename is more than the one field: the surname the Roster sorts on
    // follows it, and so does every other member whose preferred partners
    // named them — otherwise those requests quietly stop resolving. Null
    // means the name is unchanged, and nothing beyond this form is touched.
    const rename = planRename(player, players, draft.name);
    if (rename) rename.partners.forEach(u => updatePlayer(u.id, u.changes));

    updatePlayer(player.id, {
      ...draft,
      // Keeps the stored name byte-identical when only its spacing was
      // touched, rather than writing a cosmetic change to the schedule.
      ...(rename ? rename.player.changes : { name: player.name }),
      email,
      householdId: draft.householdId.trim(),
      age: draft.age === "" ? null : Number(draft.age),
    });
    setEditing(false);
    setDraft(null);
    setEmailError(null);
    setNameError(null);
  }

  function toggleAvailability(day, time) {
    const key = `${day}|${time}`;
    setDraft(prev => {
      const set = new Set(prev.availability);
      if (set.has(key)) set.delete(key);
      else set.add(key);
      return { ...prev, availability: [...set] };
    });
  }

  // Groups this member is queued for. Independent of currentGroup — being in
  // one group and waiting for another is the normal case.
  const waitingFor = groupsWaitingFor(groups, player.id);
  // Offer every group they aren't already in and aren't already queued for.
  const waitlistable = groups.filter(g =>
    g.id !== player.groupId &&
    !(g.playerIds || []).includes(player.id) &&
    !isWaiting(g, player.id)
  );

  const wantsPrivate = player.privateTimes &&
    !player.privateTimes.toLowerCase().includes("don't want");

  // What saving the name would do beyond this member's own record, worked out
  // as it's typed. A rename that reaches other people's records shouldn't be
  // something you only discover afterwards.
  const pendingRename = editing && draft ? planRename(player, players, draft.name) : null;
  const nameClashes = editing && draft ? duplicateNames(players, player.id, draft.name) : [];
  const renameEffect = (() => {
    if (nameClashes.length > 0) {
      return `Heads up: ${nameClashes.length === 1 ? "another member is" : `${nameClashes.length} other members are`} already called this.`;
    }
    if (!pendingRename || pendingRename.partners.length === 0) return null;
    const n = pendingRename.partners.length;
    return `Also updates ${n} member${n === 1 ? "" : "s"} who asked to play with them: ${pendingRename.partners.map(u => u.name).join(", ")}.`;
  })();

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col overflow-hidden"
        style={{ maxHeight: "90vh" }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`${colors.header} ${colors.text} px-5 py-4 flex-shrink-0 flex flex-wrap items-start justify-between gap-2`}>
          <div className="min-w-0">
            {editing ? (
              <div className="mb-1">
                <input
                  type="text"
                  value={draft.name}
                  onChange={e => { setDraft(prev => ({ ...prev, name: e.target.value })); setNameError(null); }}
                  aria-label="Member name"
                  className={`bg-white text-gray-800 text-lg font-bold leading-tight rounded px-2 py-1 w-full max-w-sm border focus:outline-none focus:ring-2 ${
                    nameError ? "border-red-400 focus:ring-red-400" : "border-transparent focus:ring-blue-400"
                  }`}
                />
                {nameError && <p className="text-xs font-medium mt-1">{nameError}</p>}
                {!nameError && renameEffect && (
                  <p className="text-xs opacity-80 mt-1">{renameEffect}</p>
                )}
              </div>
            ) : (
              <div className="text-lg font-bold leading-tight break-words">{player.name}</div>
            )}
            <div className="text-sm font-medium opacity-80 mt-0.5">{player.tier}</div>
            <div className="flex flex-wrap items-center gap-3 text-xs mt-1.5 opacity-75">
              {player.age != null && <span>Age {player.age}</span>}
              {player.email && <span className="break-all">{player.email}</span>}
              {!editing && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => updatePlayer(player.id, { gender: "Male" })}
                    title="Set gender to Male"
                    className={`w-5 h-5 flex items-center justify-center rounded-full text-sm leading-none transition-colors ${
                      player.gender === "Male" ? "bg-blue-500 text-white" : "bg-white bg-opacity-40 hover:bg-opacity-70"
                    }`}
                  >
                    ♂
                  </button>
                  <button
                    onClick={() => updatePlayer(player.id, { gender: "Female" })}
                    title="Set gender to Female"
                    className={`w-5 h-5 flex items-center justify-center rounded-full text-sm leading-none transition-colors ${
                      player.gender === "Female" ? "bg-pink-500 text-white" : "bg-white bg-opacity-40 hover:bg-opacity-70"
                    }`}
                  >
                    ♀
                  </button>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {!editing && (
              <button
                onClick={startEditing}
                className="text-xs font-medium opacity-70 hover:opacity-100 border border-current rounded px-2 py-1 transition-opacity"
              >
                Edit
              </button>
            )}
            <button onClick={onClose} className="text-2xl leading-none opacity-60 hover:opacity-100 transition-opacity">×</button>
          </div>
        </div>

        {/* Body — two columns on desktop. Left is who this member is and who
            they are with right now; right is where they might go next, with
            the group finder at the top since that is what this card is
            mostly opened to do. Stacks to one column below lg. */}
        <div className="overflow-y-auto flex-1 p-5">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-6 gap-y-5 items-start">
            <div className="space-y-5 min-w-0">
            {/* Age & Roster */}
            {editing && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <SectionLabel>Age</SectionLabel>
                  <input
                    type="number"
                    min="0"
                    value={draft.age}
                    onChange={e => setDraft(prev => ({ ...prev, age: e.target.value }))}
                    placeholder="—"
                    className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                </div>
                <div>
                  <SectionLabel>Roster</SectionLabel>
                  <select
                    value={draft.tier}
                    onChange={e => setDraft(prev => ({ ...prev, tier: e.target.value }))}
                    className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                  >
                    {TIER_ORDER.map(t => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <SectionLabel>Gender</SectionLabel>
                  <select
                    value={draft.gender}
                    onChange={e => setDraft(prev => ({ ...prev, gender: e.target.value }))}
                    className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                  >
                    <option value="">— Not specified —</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
              </div>
            )}

            {/* Contact — the address this member is reached at, both for the
                schedule mail-out and the public "My Schedule" lookup. Everyone
                sharing a household id can look each other's schedule up, so a
                member with no address of their own is still reachable through
                a parent or spouse. */}
            <div>
              <SectionLabel>Email</SectionLabel>
              {editing ? (
                <>
                  <input
                    type="email"
                    value={draft.email}
                    onChange={e => { setDraft(prev => ({ ...prev, email: e.target.value })); setEmailError(null); }}
                    placeholder="name@example.com"
                    autoComplete="off"
                    className={`border rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-2 ${
                      emailError ? "border-red-400 focus:ring-red-400" : "border-gray-300 focus:ring-blue-400"
                    }`}
                  />
                  {emailError && <p className="text-xs text-red-600 mt-1">{emailError}</p>}
                  <div className="mt-2">
                    <SectionLabel>Household ID</SectionLabel>
                    <input
                      type="text"
                      value={draft.householdId}
                      onChange={e => setDraft(prev => ({ ...prev, householdId: e.target.value }))}
                      placeholder="e.g. 032 — blank means no family link"
                      className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Members sharing an id see each other's schedules on the My Schedule page.
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-700 break-all">
                    {player.email || <span className="italic text-gray-400">No email on file</span>}
                  </p>
                  {household.length > 0 ? (
                    <p className="text-xs text-gray-500 mt-1">
                      Household {player.householdId} with{" "}
                      {household.map(p => `${p.name}${p.email ? "" : " (no email)"}`).join(", ")}
                    </p>
                  ) : !player.email && (
                    <p className="text-xs text-amber-600 mt-1">
                      No way to reach this member — add an address, or a household id shared with a parent or spouse.
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Skill level */}
            <div>
              <SectionLabel>Skill Level</SectionLabel>
              {editing ? (
                <input
                  type="text"
                  value={draft.skillLevel}
                  onChange={e => setDraft(prev => ({ ...prev, skillLevel: e.target.value }))}
                  className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              ) : (
                <p className="text-sm text-gray-700">{player.skillLevel || <span className="italic text-gray-400">None listed</span>}</p>
              )}
            </div>

            {/* Current group */}
            {!editing && (
              <div>
                <SectionLabel>Current Group</SectionLabel>
                {currentGroup ? (
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <button
                        onClick={() => { onClose(); onOpenGroup?.(currentGroup.id); }}
                        title={`Open ${currentGroup.id}`}
                        className="text-sm text-gray-700 text-left hover:text-blue-600 transition-colors group/cg"
                      >
                        <span className="font-mono font-bold group-hover/cg:underline">{currentGroup.id}</span>
                        {currentGroup.day && getGroupSchedule(currentGroup).startTime && (
                          <span className="text-gray-500">
                            {" "}— {currentGroup.day} · {getGroupSchedule(currentGroup).startTime}–{getEndTime(currentGroup)} · {currentGroup.court}
                          </span>
                        )}
                        <span className="text-gray-400 ml-1" aria-hidden="true">↗</span>
                      </button>
                    </div>
                    {/* Leaves the card open on purpose: taking someone out is
                        usually the first half of moving them, and the group
                        finder is right there in the next column. */}
                    <button
                      onClick={() => onRemoveFromGroup?.(player.id, currentGroup.id)}
                      title={`Take ${player.name} out of ${currentGroup.id}`}
                      className="text-xs text-gray-400 hover:text-red-600 transition-colors whitespace-nowrap flex-shrink-0"
                    >
                      Remove
                    </button>
                  </div>
                ) : isDeferred(player) ? (
                  <p className="text-sm text-slate-600">
                    Waiting for next term — not being placed.
                  </p>
                ) : hasNoFit(player) ? (
                  <p className="text-sm text-amber-700">
                    Can't make any clinic time — wants to play, but nothing on
                    the schedule fits.
                  </p>
                ) : (
                  <p className="text-sm text-gray-400 italic">Unassigned</p>
                )}

                {extraClinics.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-gray-500 mb-1">Also in:</p>
                    <ul className="space-y-1">
                      {extraClinics.map(g => (
                        <li key={g.id} className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <button
                              onClick={() => { onClose(); onOpenGroup?.(g.id); }}
                              title={`Open ${g.id}`}
                              className="text-sm text-gray-700 text-left hover:text-blue-600 transition-colors"
                            >
                              <span className="font-mono font-bold">{g.id}</span>
                              {g.day && getGroupSchedule(g).startTime && (
                                <span className="text-gray-500">
                                  {" "}— {g.day} · {getGroupSchedule(g).startTime}–{getEndTime(g)} · {g.court}
                                </span>
                              )}
                              <span className="text-gray-400 ml-1" aria-hidden="true">↗</span>
                            </button>
                          </div>
                          <button
                            onClick={() => onRemoveFromGroup?.(player.id, g.id)}
                            title={`Take ${player.name} out of ${g.id}`}
                            className="text-xs text-gray-400 hover:text-red-600 transition-colors whitespace-nowrap flex-shrink-0"
                          >
                            Remove
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                )}

                {/* Only offered when they aren't in a group: someone already
                    placed is neither sitting the term out nor short of a slot
                    that fits. Assigning them clears both, in useKACState.
                    The two are mutually exclusive — turning one on turns the
                    other off, since they're competing answers to the same
                    question of why this member has no group. */}
                {!currentGroup && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    <button
                      onClick={() => updatePlayer(player.id, isDeferred(player)
                        ? { deferred: undefined }
                        : { deferred: true, noFit: undefined })}
                      className={`text-xs px-2 py-1 rounded border transition-colors ${
                        isDeferred(player)
                          ? "bg-slate-600 border-slate-600 text-white"
                          : "bg-white border-gray-300 text-gray-600 hover:border-gray-400"
                      }`}
                      title={isDeferred(player)
                        ? "Put them back in the list of members needing a group"
                        : "They've said they'll wait for next term — take them out of the unassigned list"}
                    >
                      {isDeferred(player) ? "✓ Waiting for next term" : "+ Waiting for next term"}
                    </button>
                    <button
                      onClick={() => updatePlayer(player.id, hasNoFit(player)
                        ? { noFit: undefined }
                        : { noFit: true, deferred: undefined })}
                      className={`text-xs px-2 py-1 rounded border transition-colors ${
                        hasNoFit(player)
                          ? "bg-amber-600 border-amber-600 text-white"
                          : "bg-white border-gray-300 text-gray-600 hover:border-gray-400"
                      }`}
                      title={hasNoFit(player)
                        ? "Put them back in the list of members needing a group"
                        : "No clinic on the schedule works for them — take them out of the unassigned list and flag the demand"}
                    >
                      {hasNoFit(player) ? "✓ Can't make any clinic" : "+ Can't make any clinic"}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Changes someone has asked for about this member. Shown on the
                card because that's where you look when you wonder why they
                aren't where you expected. */}
            {!editing && pendingRequests.length > 0 && (
              <PendingRequestList
                requests={pendingRequests}
                currentUser={currentUser}
                players={players}
                groups={groups}
                onApprove={onApproveRequest}
                onReject={onRejectRequest}
                onCancel={onCancelRequest}
              />
            )}

            {/* Group Members */}
            {!editing && groupMembers.length > 0 && (
              <div>
                <SectionLabel>Group Members</SectionLabel>
                <div className="space-y-1.5">
                  {groupMembers.map(p => {
                    const gc = TIER_COLORS[p.tier] || TIER_COLORS["No Group"];
                    return (
                      <div key={p.id} className={`${gc.bg} ${gc.border} border rounded-lg px-3 py-2 flex items-center justify-between`}>
                        <div>
                          <div className="font-medium text-gray-800 text-sm">{p.name}</div>
                          <div className="text-xs text-gray-500">{p.skillLevel}</div>
                        </div>
                        {p.age != null && <span className="text-xs text-gray-400 flex-shrink-0">Age {p.age}</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Availability */}
            <div>
              <SectionLabel>Availability</SectionLabel>
              {editing ? (
                <div className="space-y-2">
                  {DAYS.map(day => (
                    <div key={day}>
                      <div className="text-xs font-semibold text-gray-500 mb-1">{day}</div>
                      <div className="flex flex-wrap gap-1.5">
                        {TIME_SLOTS.map(time => {
                          const key = `${day}|${time}`;
                          const checked = draft.availability.includes(key);
                          return (
                            <label
                              key={time}
                              className={`flex items-center gap-1 text-xs px-2 py-1 rounded border cursor-pointer transition-colors ${
                                checked
                                  ? `${colors.header} ${colors.border} ${colors.text}`
                                  : "bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggleAvailability(day, time)}
                                className="sr-only"
                              />
                              {time}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                !hasDays ? (
                  <p className="text-sm text-gray-400 italic">None listed</p>
                ) : (
                  <div className="space-y-1.5">
                    {DAYS.filter(d => availByDay[d].length > 0).map(day => (
                      <div key={day} className="flex gap-2 text-xs items-start">
                        <span className="font-semibold text-gray-600 w-20 flex-shrink-0 pt-0.5">{day}</span>
                        <div className="flex flex-wrap gap-1">
                          {availByDay[day].map(time => (
                            <span key={time} className={`${colors.bg} ${colors.border} border rounded px-1.5 py-0.5 text-gray-700`}>
                              {time}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>

            {/* Preferred partners */}
            <div>
              <SectionLabel>Preferred Partners</SectionLabel>
              {editing ? (
                <input
                  type="text"
                  value={draft.preferredPartners}
                  onChange={e => setDraft(prev => ({ ...prev, preferredPartners: e.target.value }))}
                  placeholder="e.g. John Smith, Jane Doe"
                  className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              ) : (
                player.preferredPartners &&
                !["no", "n/a", "not sure yet"].includes(player.preferredPartners.toLowerCase().trim()) ? (
                  <p className="text-sm text-gray-700">{player.preferredPartners}</p>
                ) : (
                  <p className="text-sm text-gray-400 italic">None listed</p>
                )
              )}
            </div>

            </div>
            <div className="space-y-5 min-w-0">
            {/* Assign to group — hidden while editing */}
            {!editing && (
              <div>
                <SectionLabel>Assign to Group</SectionLabel>
                <input
                  type="text"
                  placeholder="Search by group ID, tier, day, or player name…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full mb-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
                <div className="space-y-1 max-h-[26rem] overflow-y-auto pr-1">
                  {filteredGroups.map(g => {
                    const gc = TIER_COLORS[g.tier] || TIER_COLORS["No Group"];
                    const isCurrent = (g.playerIds || []).includes(player.id);
                    const isFull = g.playerIds.length >= 4;
                    const conflict = hasAvailabilityConflict(player, g);
                    const gSchedule = getGroupSchedule(g);
                    const members = groupMembersOf(g);
                    return (
                      <button
                        key={g.id}
                        onClick={() => handleAssign(g)}
                        disabled={isCurrent}
                        className={`
                          w-full text-left rounded px-3 py-2 text-xs flex items-start justify-between gap-2 border
                          transition-all
                          ${isCurrent
                            ? "opacity-40 cursor-default bg-gray-50 border-gray-200"
                            : `${gc.bg} ${gc.border} hover:brightness-95 cursor-pointer`
                          }
                        `}
                      >
                        <div className="min-w-0">
                          <span className={`font-mono font-bold ${gc.text}`}>{g.id}</span>
                          <span className="text-gray-500 ml-2">{g.tier}</span>
                          {g.day && gSchedule.startTime && (
                            <span className="text-gray-400 ml-2 truncate">· {g.day} · {gSchedule.startTime}–{getEndTime(g)}</span>
                          )}
                          {conflict && (
                            <span className="text-amber-500 ml-1" title="Player not available at this time slot">⚠</span>
                          )}
                          {members.length > 0 && (
                            <div className="text-gray-500 mt-0.5 truncate">
                              {members.map((p, i) => (
                                <span
                                  key={p.id}
                                  className={searchQuery && p.name.toLowerCase().includes(searchQuery) ? "font-semibold text-gray-700" : ""}
                                >
                                  {p.name}{p.age != null ? ` (${p.age})` : ""}{i < members.length - 1 ? ", " : ""}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                        <span className={`flex-shrink-0 px-1.5 py-0.5 rounded font-semibold text-xs ${isFull ? "bg-green-100 text-green-700" : "bg-amber-50 text-amber-700"}`}>
                          {g.playerIds.length}/4
                        </span>
                      </button>
                    );
                  })}
                  {filteredGroups.length === 0 && (
                    <p className="text-sm text-gray-400 italic px-1">No groups match.</p>
                  )}
                </div>
              </div>
            )}
            {/* Waiting for — group waitlists this member is on. Separate from
                "Assign to Group" above: nothing here places them anywhere. */}
            {!editing && (
              <div>
                <SectionLabel>Waiting for</SectionLabel>
                {waitingFor.length === 0 ? (
                  <p className="text-sm text-gray-400 italic mb-1.5">Not waiting for any group.</p>
                ) : (
                  <ul className="space-y-1 mb-1.5">
                    {waitingFor.map(g => (
                      <li key={g.id} className="flex items-center gap-2 text-sm bg-slate-50 border border-dashed border-slate-300 rounded px-2.5 py-1.5">
                        <span className="font-mono text-xs text-slate-700">{g.id}</span>
                        <span className="text-gray-600 flex-1 min-w-0 truncate">
                          {g.tier}
                          {g.day && ` · ${g.day}`}
                        </span>
                        <span className="text-xs text-slate-600 whitespace-nowrap">
                          #{waitlistPosition(g, player.id)} in line
                          {hasSpace(g) && <span className="text-green-600 font-medium"> · space now</span>}
                        </span>
                        <button
                          onClick={() => onWaitlistRemove?.(g.id, player.id)}
                          title="Take off this waitlist"
                          className="text-gray-300 hover:text-red-500 text-lg leading-none"
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <select
                  value=""
                  onChange={e => { if (e.target.value) onWaitlistAdd?.(e.target.value, player.id); }}
                  className="border border-gray-300 rounded px-2 py-1 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                >
                  <option value="">+ Add to a group's waitlist…</option>
                  {waitlistable.map(g => (
                    <option key={g.id} value={g.id}>
                      {g.id} · {g.tier}{g.day ? ` · ${g.day}` : ""} ({(g.playerIds || []).length}/4)
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Follow-up tags — set while talking to the member, cleared by hand
                once they've been dealt with. Hidden in Edit mode (like the
                gender buttons) because they save on click, not on Save. */}
            {!editing && (
              <div>
                <SectionLabel>Follow-up</SectionLabel>
                <div className="flex flex-wrap gap-2">
                  {FOLLOW_UPS.map(tag => {
                    const on = hasFollowUp(player, tag.key);
                    return (
                      <button
                        key={tag.key}
                        onClick={() => updatePlayer(player.id, { followUps: toggleFollowUp(player, tag.key) })}
                        title={on ? `Remove "${tag.label}"` : `Tag as "${tag.label}"`}
                        className={`text-sm px-3 py-1.5 rounded border transition-colors ${
                          on ? tag.active : "bg-white border-gray-300 text-gray-600 hover:border-gray-400"
                        }`}
                      >
                        {on ? "✓" : "+"} {tag.label}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Tagged members are filterable on the Members roster. Tags stay until you remove them.
                </p>
              </div>
            )}

            {/* Notes — what this member has told us. Editable in place. */}
            <div>
              <div className="flex items-baseline justify-between">
                <SectionLabel>Notes &amp; feedback</SectionLabel>
                {!editing && noteSaved && (
                  <span className="text-xs font-medium text-green-600 mb-1.5">Saved</span>
                )}
              </div>
              {editing ? (
                <textarea
                  value={draft.notes}
                  onChange={e => setDraft(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Any notes about this player…"
                  rows={3}
                  className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
                />
              ) : (
                <>
                  <textarea
                    value={noteDraft}
                    onChange={e => setNoteDraft(e.target.value)}
                    onBlur={commitNote}
                    onKeyDown={e => {
                      if (e.key === "Escape") {
                        e.stopPropagation();
                        setNoteDraft(player.notes || "");
                        e.target.blur();
                      }
                    }}
                    placeholder="What did they say? e.g. wants a later slot, group feels too advanced…"
                    rows={3}
                    className="border border-gray-200 bg-gray-50 hover:bg-white focus:bg-white rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400 resize-y transition-colors"
                  />
                  <p className="text-xs text-gray-400 mt-1">Saves when you click away · Esc to undo</p>
                </>
              )}
            </div>

            {/* Private lesson times */}
            <div>
              <SectionLabel>Private Lesson Times</SectionLabel>
              {editing ? (
                <input
                  type="text"
                  value={draft.privateTimes}
                  onChange={e => setDraft(prev => ({ ...prev, privateTimes: e.target.value }))}
                  placeholder="e.g. Tuesdays 5pm"
                  className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              ) : (
                wantsPrivate ? (
                  <p className="text-sm text-gray-700">{player.privateTimes}</p>
                ) : (
                  <p className="text-sm text-gray-400 italic">Not requested</p>
                )
              )}
            </div>

            </div>
          </div>

          {/* Save / Cancel when editing */}
          {editing && (
            <div className="flex gap-2 pt-1">
              <button
                onClick={saveEditing}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold py-2 rounded-lg transition-colors"
              >
                Save changes
              </button>
              <button
                onClick={cancelEditing}
                className="flex-1 border border-gray-300 hover:bg-gray-50 text-gray-600 text-sm font-medium py-2 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
      {children}
    </div>
  );
}
